import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { CircularProgress, Box, Typography } from '@material-ui/core';
import { googleAuthService } from '../../services';
import { GoogleAuthResponse } from '../../services/GoogleAuth/GoogleAuthService';

const GoogleAuthSuccess: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Retrieve the auth token from backend
        const response: GoogleAuthResponse = await googleAuthService.retrieveAuthToken();

        // Backend uses 'sc' and 'result', not 'status' and 'data'
        if (response.sc === 1 && response.result.token) {
          // Store authentication data
          googleAuthService.handleAuthSuccess(
            response.result.token,
            response.result.user
          );

          // Determine redirect path based on user data (match simple login logic)
          const user = response.result.user;
          let redirectPath = '/dashboard'; // Default
          
          // Super Admin / Admin
          if (user.RoleId === 1) {
            redirectPath = '/AdminDashboard';
          } 
          // Special companies
          else if (user.CompanyName === 'TruAge' || user.CompanyName === 'UptimeGuard') {
            redirectPath = '/uptime-monitoring';
          }
          // Regular Admin (RoleId 2)
          else if (user.RoleId === 2) {
            // Check if needs billing setup
            if (user.RemainingDays === null || user.SubscriptionStatus === null) {
              redirectPath = `/${user.CompanyName}/billing-management`;
            } 
            // Check if has accounts
            else if (user.UserAccounts && user.UserAccounts.length > 0) {
              redirectPath = `/${user.CompanyName}/dashboard`;
            } 
            // No accounts - go to create account
            else {
              redirectPath = `/${user.CompanyName}/accounts`;
            }
          }
          // DevOps role (RoleId 5)
          else if (user.RoleId === 5) {
            redirectPath = '/admin-dashboard';
          }
          // Other users (Employee, QA, etc.)
          else {
            // Check if has accounts
            if (user.UserAccounts && user.UserAccounts.length > 0) {
              redirectPath = `/${user.CompanyName}/dashboard`;
            } else {
              redirectPath = `/${user.CompanyName}/accounts`;
            }
          }
          
          // Small delay to ensure localStorage is updated
          setTimeout(() => {
            window.location.replace(redirectPath);
          }, 100);
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err: any) {
        console.error('Google Auth Error:', err);
        setError(
          err.message || 'Authentication failed. Please try again.'
        );
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          history.push('/login');
        }, 3000);
      }
    };

    handleAuthCallback();
  }, [dispatch, history]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      padding={3}
    >
      {error ? (
        <>
          <Typography variant="h6" color="error" gutterBottom>
            {error}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Redirecting to login...
          </Typography>
        </>
      ) : (
        <>
          <CircularProgress size={50} style={{ marginBottom: 20 }} />
          <Typography variant="h6" gutterBottom>
            Completing sign in...
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Please wait while we set up your account
          </Typography>
        </>
      )}
    </Box>
  );
};

export default GoogleAuthSuccess;
